import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatures } from '../../contexts/FeaturesContext';
import { brand } from '../../theme/colors';
import { Navigate } from 'react-router-dom';
import EnhancedClientDashboard from './EnhancedClientDashboard'; // New enhanced dashboard
import ContractorDashboard from './ContractorDashboard';
import ProjectManagerDashboard from './ProjectManagerDashboard'; // Project Manager dashboard
import MerchantDashboard from './MerchantDashboard'; // Merchant dashboard
import RealtorDashboard from './RealtorDashboard';
import ConveyancerDashboard from './ConveyancerDashboard';
import RealtorNotAvailablePage from '../../pages/RealtorNotAvailablePage';
import QualityInspectorDashboard from './QualityInspectorDashboard';
import VerifierDashboard from '../../pages/VerifierDashboard_Enhanced';

const DashboardLoading = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        flexDirection: 'column',
        gap: '20px'
    }}>
        <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #f3f3f3',
            borderTop: `4px solid ${brand.primary}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ fontSize: '18px', color: '#7f8c8d' }}>
            Loading your dashboard...
        </div>
        <style>
            {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}
        </style>
    </div>
);

/**
 * Dashboard Router Component
 * Routes users to the appropriate dashboard based on their role
 */
const DashboardRouter = () => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const features = useFeatures();

    // Show loading state while checking authentication
    if (isLoading) {
        return <DashboardLoading />;
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return (
            <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                maxWidth: '600px',
                margin: '0 auto'
            }}>
                <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔒</div>
                <h2 style={{ color: '#2c3e50', marginBottom: '15px' }}>
                    Authentication Required
                </h2>
                <p style={{ color: '#7f8c8d', marginBottom: '30px', fontSize: '16px' }}>
                    Please sign in to access your dashboard
                </p>
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                    <button
                        onClick={() => window.location.href = '/login'}
                        style={{
                            padding: '15px 30px',
                            backgroundColor: brand.primary,
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            textDecoration: 'none'
                        }}
                    >
                        Sign In
                    </button>
                    <button
                        onClick={() => window.location.href = '/register'}
                        style={{
                            padding: '15px 30px',
                            backgroundColor: '#27ae60',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            textDecoration: 'none'
                        }}
                    >
                        Create Account
                    </button>
                </div>
            </div>
        );
    }

    // Handle missing user data
    if (!user || !user.role) {
        return (
            <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                maxWidth: '600px',
                margin: '0 auto'
            }}>
                <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
                <h2 style={{ color: '#e74c3c', marginBottom: '15px' }}>
                    Profile Incomplete
                </h2>
                <p style={{ color: '#7f8c8d', marginBottom: '30px', fontSize: '16px' }}>
                    Your user profile is missing required information. Please complete your profile setup.
                </p>
                <button
                    onClick={() => window.location.href = '/profile'}
                    style={{
                        padding: '15px 30px',
                        backgroundColor: '#f39c12',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }}
                >
                    Complete Profile
                </button>
            </div>
        );
    }

    // Route to appropriate dashboard based on user role
    switch (user.role?.toLowerCase()) {
        case 'client':
            // Clients go straight to their dashboard. The "New Project" button
            // there opens the unified step wizard — the single project-creation
            // path. The old AI-chatbot welcome/onboarding detour was removed.
            return <EnhancedClientDashboard />;
        
        case 'contractor': {
            // Redirect first-time contractors to the welcome/onboarding page.
            // Anyone who has already onboarded (welcome seen OR onboarding done)
            // is never sent back through it.
            const contractorOnboarded = user?.profile?.welcomeSeen || user?.profile?.welcome_seen
                || user?.profile?.onboardingComplete || user?.profile?.onboarding_complete;
            if (!contractorOnboarded && !localStorage.getItem('eqaya_contractor_welcome_shown')) {
                localStorage.setItem('eqaya_contractor_welcome_shown', 'true');
                return <Navigate to="/contractor/welcome" replace />;
            }
        }
            // Application gate: a contractor only sees the platform once approved.
            // Test accounts bypass. Not-yet-submitted -> apply; submitted/rejected -> status.
            const approval = user?.approvalStatus || user?.approval_status;
            const isTest = user?.isTest === true || user?.is_test === true;
            if (!isTest && approval !== 'approved') {
                const submitted = !!(user?.applicationSubmittedAt || user?.application_submitted_at);
                return <Navigate to={submitted ? '/contractor/application-status' : '/contractor/apply'} replace />;
            }
            // Use enhanced contractor dashboard with AI assistance
            return <ContractorDashboard />;
        
        case 'admin':
            // Admins land on the new lean dashboard. The legacy console is
            // untouched and still reachable at /admin/dashboard.
            return <Navigate to="/admin/home" replace />;
        
        case 'verifier':
        case 'professional_verifier':
            // Redirect first-time verifiers to the welcome/onboarding page
            if (!user?.profile?.welcomeSeen && !localStorage.getItem('eqaya_verifier_welcome_shown')) {
                localStorage.setItem('eqaya_verifier_welcome_shown', 'true');
                return <Navigate to="/verifier/welcome" replace />;
            }
            return <VerifierDashboard />;
        
        case 'quality_inspector':
            return <QualityInspectorDashboard />;
        
        case 'project_manager':
            return <ProjectManagerDashboard />;
        
        case 'merchant': {
            // Approval gate: merchants can build their profile and upload KYC
            // documents while pending, but marketplace actions (quoting,
            // deliveries, withdrawals) are blocked server-side until an admin
            // approves the account.
            const merchantApproval = user?.approvalStatus || user?.approval_status;
            const merchantIsTest = user?.isTest === true || user?.is_test === true;
            if (!merchantIsTest && merchantApproval && merchantApproval !== 'approved') {
                return (
                    <div style={{ textAlign: 'center', padding: '60px 20px', maxWidth: 560, margin: '0 auto' }}>
                        <h2>Your supplier account is being reviewed</h2>
                        <p style={{ color: '#666', lineHeight: 1.6 }}>
                            Our team is verifying your business details. While you wait, you can
                            complete your <a href="/merchants/profile">business profile</a> and
                            upload your <a href="/merchants/documents">verification documents</a> —
                            a complete application is reviewed faster.
                        </p>
                        <p style={{ color: '#666' }}>
                            You'll be notified as soon as you're approved and can start receiving
                            quote requests and orders.
                        </p>
                    </div>
                );
            }
            return <MerchantDashboard />;
        }

        case 'realtor': {
            // The feature switch comes first. With listings off the realtor
            // endpoints 404, so neither the pending-review copy (which links
            // into /realtor/*) nor RealtorDashboard (which would show "failed
            // to load" over an empty shell) is honest here.
            if (!features.loaded) return <DashboardLoading />;
            if (!features.listings) return <RealtorNotAvailablePage />;
            // Same gate as merchant: build profile/documents while pending;
            // listing actions unlock server-side after admin approval.
            const realtorApproval = user?.approvalStatus || user?.approval_status;
            const realtorIsTest = user?.isTest === true || user?.is_test === true;
            if (!realtorIsTest && realtorApproval && realtorApproval !== 'approved') {
                return (
                    <div style={{ textAlign: 'center', padding: '60px 20px', maxWidth: 560, margin: '0 auto' }}>
                        <h2>Your realtor account is being reviewed</h2>
                        <p style={{ color: '#666', lineHeight: 1.6 }}>
                            We check every realtor against the Estate Agents Council register. While you wait, complete
                            your <a href="/realtor/profile">profile and agency</a> and upload your
                            <a href="/realtor/documents"> ID and EAC practising certificate</a> — a complete application is reviewed faster.
                        </p>
                        <p style={{ color: '#666' }}>You'll be notified as soon as you're approved and can start listing properties.</p>
                    </div>
                );
            }
            return <RealtorDashboard />;
        }

        case 'conveyancer': {
            // Same order as realtors: the feature switch first, then the review gate.
            if (!features.loaded) return <DashboardLoading />;
            if (!features.listings) return <RealtorNotAvailablePage tools="Conveyancer tools" />;
            const conveyancerApproval = user?.approvalStatus || user?.approval_status;
            const conveyancerIsTest = user?.isTest === true || user?.is_test === true;
            if (!conveyancerIsTest && conveyancerApproval && conveyancerApproval !== 'approved') {
                return (
                    <div style={{ textAlign: 'center', padding: '60px 20px', maxWidth: 560, margin: '0 auto' }}>
                        <h2>Your conveyancer account is being reviewed</h2>
                        <p style={{ color: '#666', lineHeight: 1.6 }}>
                            We check every conveyancer against the Law Society of Zimbabwe register. While you wait, pick
                            your <a href="/conveyancer/profile">Law Society entry</a> and upload your
                            <a href="/conveyancer/documents"> ID and this year's practising certificate</a> — a complete application is reviewed faster.
                        </p>
                    </div>
                );
            }
            return <ConveyancerDashboard />;
        }

        default:
            return (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '60px 20px',
                    maxWidth: '600px',
                    margin: '0 auto'
                }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>❓</div>
                    <h2 style={{ color: '#e74c3c', marginBottom: '15px' }}>
                        Unknown User Role
                    </h2>
                    <p style={{ color: '#7f8c8d', marginBottom: '20px', fontSize: '16px' }}>
                        Your account has an unrecognized role: <strong>{user.role}</strong>
                    </p>
                    <p style={{ color: '#7f8c8d', marginBottom: '30px', fontSize: '14px' }}>
                        Please contact support for assistance or try logging out and back in.
                    </p>
                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                        <button
                            onClick={() => window.location.href = '/support'}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: brand.primary,
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            Contact Support
                        </button>
                        <button
                            onClick={() => {
                                // Clear auth and redirect to login
                                localStorage.removeItem('eqaya_token');
                                localStorage.removeItem('eqaya_user');
                                window.location.href = '/login';
                            }}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#95a5a6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            Sign Out & Retry
                        </button>
                    </div>
                </div>
            );
    }
};

export default DashboardRouter;
